export function deskKey(value) {
  return String(value || '').toLowerCase().replace(/^#?(mesa_)?/, '').replace(/-/g, '_').replace(/ñ/g, 'nh');
}

export function searchableText(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export function personName(person) {
  const name = person && person.preferred_name;
  return name ? [name.given_name, name.family_name].filter(Boolean).join(' ') : '';
}

export function isActiveAssignmentForPerson(assignment, personId) {
  if (personId === null || personId === undefined || personId === '') return false;
  return !assignment.freed_status && assignment.person && String(assignment.person.id) === String(personId);
}

function hasBiography(value) {
  if (!value) return false;
  if (typeof value === 'object') return Object.values(value).some(hasBiography);
  return /<(img|video|iframe)\b/i.test(value) || String(value).replace(/<[^>]*>/g, '').replace(/&nbsp;|\s/g, '').length > 0;
}

export function daysUntil(date, now = new Date()) {
  if (!date) return Infinity;
  const parts = String(date).slice(0, 10).split('-').map(Number);
  const target = Date.UTC(parts[0], parts[1] - 1, parts[2]);
  if (parts.length !== 3 || !Number.isFinite(target)) return Infinity;
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(now);
  const fields = Object.fromEntries(today.map(part => [part.type, part.value]));
  return Math.round((target - Date.UTC(Number(fields.year), Number(fields.month) - 1, Number(fields.day))) / 86400000);
}

export function expirationState(days) {
  if (days < 0) return 'expired';
  if (days <= 30) return 'expiring';
  return 'current';
}

export function deskColor(workplace, admin) {
  if (!workplace.registered) return '#e3e5e7';
  if (!workplace.active.length) return '#ffffff';
  if (!admin) return '#c4b7c8';
  const days = workplace.days;
  if (days < 0) return '#ff6464';
  if (days <= 30) return '#ffdd09';
  if (!Number.isFinite(days)) return '#14d100';
  if (days > 1460) return '#47702c';
  if (days > 1095) return '#5e943a';
  if (days > 730) return '#75b848';
  if (days > 365) return '#8bdb56';
  return '#a2ff64';
}

export function normalizeWorkplace(raw, admin, now = new Date()) {
  const assignments = Array.isArray(raw.workplace_assignations) ? raw.workplace_assignations : [];
  const active = assignments.filter(assignment => !assignment.freed_status).map(assignment => {
    const person = assignment.person || {};
    const account = person.account || {};
    const photo = account.picture_website;
    const hasPhoto = !!photo && photo !== 'null';
    const days = admin ? daysUntil(assignment.expiration_date, now) : Infinity;
    return {
      id: assignment.id, personId: person.id, name: personName(person),
      expiration: admin ? assignment.expiration_date : null,
      days, state: expirationState(days), photo: hasPhoto && (admin || (!account.picture_website_temporary && !account.picture_website_private)) ? photo : null,
      temporaryPhoto: admin && account.picture_website_temporary === true,
      needsPhoto: admin && (!hasPhoto || account.picture_website_temporary === true),
      needsBio: admin && !hasBiography((person.publicprofile || {}).text_website)
    };
  });
  const days = active.length ? Math.min(...active.map(assignment => assignment.days)) : Infinity;
  return {
    ...raw, key: deskKey(raw.place_code), registered: raw.id != null, active, days,
    state: raw.id == null ? 'unregistered' : active.length ? (admin ? expirationState(days) : 'occupied') : 'free',
    photoPending: active.filter(assignment => assignment.needsPhoto).length,
    bioPending: active.filter(assignment => assignment.needsBio).length,
    history: admin ? assignments.filter(assignment => assignment.freed_status).map(assignment => ({
      id: assignment.id, personId: (assignment.person || {}).id,
      name: personName(assignment.person), expiration: assignment.expiration_date
    })).sort((a, b) => String(b.expiration || '').localeCompare(String(a.expiration || ''))) : []
  };
}

export function matchesWorkplace(workplace, query, status, admin) {
  const host = workplace.host || {};
  const haystack = searchableText([
    workplace.place_code, workplace.name, workplace.room, workplace.locationName,
    workplace.file_cabinet, workplace.network_socket, workplace.extension,
    host.hostname, host.ip, host.mac, ...workplace.active.map(assignment => assignment.name)
  ].filter(Boolean).join(' '));
  const words = searchableText(query).trim().split(/\s+/).filter(Boolean);
  if (!words.every(word => haystack.includes(word))) return false;
  if (status === 'occupied' && !workplace.active.length) return false;
  if (status === 'free' && workplace.active.length) return false;
  if (status === 'shared' && workplace.active.length < 2) return false;
  if (admin && ['expired', 'expiring'].includes(status) && workplace.state !== status) return false;
  if (admin && status === 'photoPending' && !workplace.photoPending) return false;
  if (admin && status === 'bioPending' && !workplace.bioPending) return false;
  return true;
}

export function photoUrl(endpoint, path, original = false) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${endpoint.replace(/\/$/, '')}/${original ? 'media' : 'styledMedia/thumb'}/${path.replace(/^\/?(media\/)?/, '')}`;
}
